import React, { useState, useRef, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useVerifyOtpMutation, useResendOtpMutation } from "../services/appApi";
import "./VerifyEmail.css";

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 60;

function VerifyEmail() {
  const location = useLocation();
  const navigate = useNavigate();

  // Email passed from Signup via router state
  const email = location.state?.email || "";

  // Guard: redirect to signup if someone lands here directly without an email
  useEffect(() => {
    if (!email) navigate("/signup");
  }, [email, navigate]);

  // ── OTP digit state ──────────────────────────────────────────────────────
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(""));
  const inputRefs = useRef([]);

  // ── API mutations ────────────────────────────────────────────────────────
  const [verifyOtp, { isLoading: isVerifying }] = useVerifyOtpMutation();
  const [resendOtp, { isLoading: isResending }] = useResendOtpMutation();

  // ── UI state ─────────────────────────────────────────────────────────────
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [hasError, setHasError] = useState(false);

  // ── Cooldown timer ────────────────────────────────────────────────────────
  // Starts at 60 immediately because an email was just sent during signup.
  // The user must wait the full cooldown before requesting another code.
  const [timer, setTimer] = useState(RESEND_COOLDOWN_SECONDS);

  useEffect(() => {
    // No interval needed when already at zero
    if (timer <= 0) return;

    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Cleanup on unmount or when timer changes
    return () => clearInterval(interval);
  }, [timer]);

  // ── Digit input handlers ─────────────────────────────────────────────────
  const handleChange = (e, idx) => {
    const val = e.target.value.replace(/\D/g, "");
    if (!val) return;

    const newDigits = [...digits];

    if (val.length > 1) {
      // Handle paste dispatched into a single box
      const pasted = val.slice(0, OTP_LENGTH);
      for (let i = 0; i < pasted.length; i++) {
        if (idx + i < OTP_LENGTH) newDigits[idx + i] = pasted[i];
      }
      setDigits(newDigits);
      inputRefs.current[Math.min(idx + pasted.length, OTP_LENGTH - 1)]?.focus();
      return;
    }

    newDigits[idx] = val;
    setDigits(newDigits);
    setHasError(false);
    setErrorMsg("");

    if (idx < OTP_LENGTH - 1) {
      inputRefs.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const newDigits = [...digits];
      if (digits[idx]) {
        newDigits[idx] = "";
        setDigits(newDigits);
      } else if (idx > 0) {
        newDigits[idx - 1] = "";
        setDigits(newDigits);
        inputRefs.current[idx - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    } else if (e.key === "ArrowRight" && idx < OTP_LENGTH - 1) {
      inputRefs.current[idx + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!text) return;
    const newDigits = Array(OTP_LENGTH).fill("");
    for (let i = 0; i < OTP_LENGTH; i++) newDigits[i] = text[i] || "";
    setDigits(newDigits);
    inputRefs.current[Math.min(text.length, OTP_LENGTH - 1)]?.focus();
  };

  // ── Auto-submit when all 6 digits are filled ─────────────────────────────
  useEffect(() => {
    const allFilled = digits.every((d) => d !== "");
    if (!allFilled) return;
    const t = setTimeout(() => {
      document
        .getElementById("verify-submit-btn")
        ?.dispatchEvent(new MouseEvent("click", { bubbles: true }));
    }, 80);
    return () => clearTimeout(t);
  }, [digits]);

  // ── Verify handler ───────────────────────────────────────────────────────
  const handleVerify = useCallback(
    async (e) => {
      e.preventDefault();
      const otp = digits.join("");

      if (otp.length < OTP_LENGTH) {
        setErrorMsg("Please enter all 6 digits.");
        setHasError(true);
        return;
      }

      setErrorMsg("");
      setSuccessMsg("");

      const { data, error } = await verifyOtp({ email, otp });

      if (data?.success) {
        if (data.token) localStorage.setItem("userToken", data.token);
        setSuccessMsg("✓ Email verified! Taking you to the chat…");
        setTimeout(() => navigate("/chat"), 1200);
      } else {
        const msg =
          error?.data?.error ||
          data?.error ||
          "Something went wrong. Please try again.";

        // Append hint when the code has expired
        const fullMsg =
          error?.data?.expired
            ? msg + ' Click "Resend code" below to get a new one.'
            : msg;

        setErrorMsg(fullMsg);
        setHasError(true);

        // Clear digits so the user can retype cleanly
        setDigits(Array(OTP_LENGTH).fill(""));
        setTimeout(() => {
          inputRefs.current[0]?.focus();
          setHasError(false);
        }, 500);
      }
    },
    [digits, email, navigate, verifyOtp]
  );

  // ── Resend handler ───────────────────────────────────────────────────────
  // Failsafe guard: timer > 0 means the button should already be disabled/hidden,
  // but we check again here to prevent any accidental double-calls.
  const handleResend = async () => {
    if (timer > 0 || isResending) return;

    setErrorMsg("");
    setSuccessMsg("");

    const { data, error } = await resendOtp({ email });

    if (data?.success) {
      setSuccessMsg("✓ A new code has been sent to your inbox.");
      setTimer(RESEND_COOLDOWN_SECONDS); // restart the 60-second countdown
      setDigits(Array(OTP_LENGTH).fill(""));
      inputRefs.current[0]?.focus();
    } else {
      const msg =
        error?.data?.error ||
        data?.error ||
        "Could not resend. Please wait and try again.";
      setErrorMsg(msg);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="verify-email-root">
      <div className="verify-email-card">

        {/* Animated icon */}
        <div className="verify-email-icon-wrap" aria-hidden="true">
          ✉️
        </div>

        <h1 className="verify-email-title">Check your email</h1>
        <p className="verify-email-subtitle">
          We sent a 6-digit code to <strong>{email}</strong>.
          Enter it below to activate your account.
        </p>

        {/* Feedback banners */}
        {errorMsg && (
          <div className="verify-email-error" role="alert">
            <span aria-hidden="true">⚠️</span> {errorMsg}
          </div>
        )}
        {successMsg && (
          <div className="verify-email-success" role="status">
            {successMsg}
          </div>
        )}

        {/* OTP input row */}
        <form onSubmit={handleVerify} noValidate>
          <div className="otp-input-row" onPaste={handlePaste}>
            {digits.map((d, idx) => (
              <input
                key={idx}
                ref={(el) => (inputRefs.current[idx] = el)}
                id={`otp-digit-${idx}`}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={d}
                autoFocus={idx === 0}
                autoComplete="one-time-code"
                className={[
                  "otp-digit",
                  d ? "otp-digit--filled" : "",
                  hasError ? "otp-digit--error" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onChange={(e) => handleChange(e, idx)}
                onKeyDown={(e) => handleKeyDown(e, idx)}
                aria-label={`Digit ${idx + 1} of ${OTP_LENGTH}`}
              />
            ))}
          </div>

          <button
            id="verify-submit-btn"
            type="submit"
            className="verify-btn"
            disabled={isVerifying || digits.some((d) => !d)}
          >
            {isVerifying ? (
              <>
                <span className="btn-spinner" aria-hidden="true" />
                Verifying…
              </>
            ) : (
              "Verify email"
            )}
          </button>
        </form>

        {/* Resend row */}
        <div className="resend-row">
          Didn't receive it?{" "}
          {timer > 0 ? (
            // Timer active: show countdown, button is hidden
            <span className="resend-cooldown">
              Resend in{" "}
              <span className="resend-countdown-value">{timer}s</span>
            </span>
          ) : (
            // Timer expired: show clickable button
            <button
              type="button"
              className="resend-btn"
              onClick={handleResend}
              disabled={isResending}
            >
              {isResending ? "Sending…" : "Resend code"}
            </button>
          )}
        </div>

      </div>
    </div>
  );
}

export default VerifyEmail;
